// Meeting Data Service for Table Storage + Blob access via backend API
import { appInsights } from './appInsights';

// New backend response structure (Table Storage)
export interface MeetingDataResponse {
  meeting: {
    partitionKey: string;
    rowKey: string;
    eTag?: string;
    id: string;
    title: string;
    tldr?: string;
    executiveSummary?: string;
    detailedSummary?: string;
    blobFileName: string;
    isProcessed: boolean;
    createdAt?: string;
    updatedAt?: string;
    updatedBy?: string;
  };
  tasks: any[];
  takeaways: any[];
}

// Legacy format that the UI currently expects
export interface MeetingData {
  meeting_brief: any;
  key_points: any[];
  actions: any[];
  themes: any[];
}

/**
 * Parse JSON string to object, return null if invalid
 */
function safeJsonParse(value: any): any {
  // If it's null or undefined, return null
  if (value === null || value === undefined) return null;
  
  // If it's already an object or array (from camelCase JSON serialization), return as-is
  if (typeof value === 'object') return value;
  
  // If it's a string, try to parse it (backward compatibility with old data)
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  
  // For any other type, return as-is
  return value;
}

/**
 * Transform new backend response (Table Storage) to legacy UI format
 * This adapter allows the UI to continue using the old format while the backend uses the new structure
 */
function transformBackendResponseToUIFormat(response: MeetingDataResponse): MeetingData {
  // Transform tasks: parse JSON strings back to objects
  const transformedTasks = (response.tasks || []).map((task: any) => ({
    ...task,
    owner: safeJsonParse(task.owner),
    requester: safeJsonParse(task.requester),
    tags: safeJsonParse(task.tags) || [],
    evidence: safeJsonParse(task.evidence) || [],
    ownerCandidates: safeJsonParse(task.ownerCandidates) || [],
    isProcessed: task.isProcessed || false,
    sentToSalesforceAt: task.sentToSalesforceAt,
    sentToSalesforceBy: task.sentToSalesforceBy
  }));

  // Transform takeaways: parse JSON strings back to objects
  const transformedTakeaways = (response.takeaways || []).map((takeaway: any) => ({
    ...takeaway,
    tags: safeJsonParse(takeaway.tags) || [],
    evidence: safeJsonParse(takeaway.evidence) || []
  }));

  return {
    meeting_brief: {
      title: response.meeting.title,
      tldr: response.meeting.tldr || '',
      executive_summary: response.meeting.executiveSummary || '',
      detailed_summary: response.meeting.detailedSummary || '',
      participants: [], // Not stored in Table Storage
      main_themes: [], // Not stored in Table Storage
      next_steps: '' // Not stored in Table Storage
    },
    key_points: transformedTakeaways,
    actions: transformedTasks,
    themes: [] // Not stored in Table Storage (immutable field from original blob)
  };
}

class MeetingDataService {
  /**
   * Load meeting data from blob storage via backend API
   */
  async loadMeeting(meetingId: string, retryCount = 0): Promise<MeetingData> {
    try {
      const startTime = Date.now();
      
      // Call the unified facade endpoint that returns everything in one call
      const response = await fetch(
        `/api/internal/meeting/${meetingId}/items`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // Include cookies for authentication
        }
      );

      // Track the dependency call
      const totalTime = Date.now() - startTime;
      appInsights.trackDependency(
        `get-meeting-${meetingId}`,
        'GET',
        `/api/internal/meeting`,
        `/api/internal/meeting/${meetingId}/items`,
        totalTime,
        response.ok,
        response.status
      );

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('MEETING_NOT_FOUND');
        }
        if (response.status === 401) {
          throw new Error('UNAUTHORIZED');
        }
        throw new Error(`Failed to load meeting: HTTP ${response.status}`);
      }

      const responseData = await response.json();
      
      // Extract the meeting data from the API response wrapper
      const backendData = responseData.data || responseData;
      
      // Transform new backend format (Table Storage) to legacy UI format
      const meetingData = transformBackendResponseToUIFormat(backendData as MeetingDataResponse);
      
      // Track successful load
      appInsights.trackEvent('MeetingDataLoaded', {
        meetingId,
        tasksCount: meetingData.actions?.length || 0,
        takeawaysCount: meetingData.key_points?.length || 0,
        duration: totalTime
      });
      
      return meetingData;

    } catch (error) {
      // Track the error in Application Insights
      if (error instanceof Error) {
        appInsights.trackException(error, {
          meetingId,
          retryCount,
          operation: 'loadMeeting'
        });

        if (error.message === 'MEETING_NOT_FOUND') {
          appInsights.trackEvent('MeetingNotFound', { meetingId });
          throw error;
        }
        
        if (error.message === 'UNAUTHORIZED') {
          appInsights.trackEvent('UnauthorizedAccess', { meetingId });
          throw error;
        }
      }
      throw error;
    }
  }

  /**
   * Validate meeting ID format (prefix or full ID)
   */
  isValidMeetingId(meetingId: string): boolean {
    // Allow any non-empty string (prefix like "abc123" or full ID like "abc123_meeting456")
    // Just check it's not empty and contains alphanumeric/underscore/hyphen
    if (!meetingId || meetingId.trim().length === 0) {
      return false;
    }
    const validFormat = /^[a-zA-Z0-9_-]+$/;
    return validFormat.test(meetingId.trim());
  }

  /**
   * Update meeting items (tasks, takeaways, and summaries)
   */
  async updateMeetingItems(
    meetingId: string,
    updates: {
      newTasks?: any[];
      tasks?: any[];
      takeaways?: any[];
      tldr?: string;
      executiveSummary?: string;
      detailedSummary?: string;
      updatedBy?: string;
    }
  ): Promise<any> {
    try {
      const startTime = Date.now();

      const response = await fetch(
        `/api/internal/meeting/${meetingId}/items`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(updates),
        }
      );

      const totalTime = Date.now() - startTime;
      appInsights.trackDependency(
        `update-meeting-${meetingId}`,
        'PUT',
        `/api/internal/meeting`,
        `/api/internal/meeting/${meetingId}/items`,
        totalTime,
        response.ok,
        response.status
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to update meeting: HTTP ${response.status}`);
      }

      const result = await response.json();

      appInsights.trackEvent('MeetingItemsUpdated', {
        meetingId,
        tasksUpdated: updates.tasks?.length || 0,
        takeawaysUpdated: updates.takeaways?.length || 0,
        summariesUpdated: !!(updates.tldr || updates.executiveSummary || updates.detailedSummary),
        duration: totalTime
      });

      return result;
    } catch (error) {
      if (error instanceof Error) {
        appInsights.trackException(error, {
          meetingId,
          operation: 'updateMeetingItems'
        });
      }
      throw error;
    }
  }
}

export const meetingDataService = new MeetingDataService();
